/**
 * Supabase Time Period Service
 * Provides functions to interact with the time_periods table in Supabase
 * Matches SQL Server TimePeriod table structure
 */

import { supabase } from './client';

export interface SupabaseTimePeriod {
  id: string;                   // ID from SQL Server (e.g., "W2515", "M2504")
  period_name: string | null;   // PeriodName (e.g., "Week 15", "April 2025")
  period_type: string | null;   // PeriodType (e.g., "Weekly", "Monthly", "Quarterly")
  current_year: number | null;  // CurrentYear (e.g., 2025)
  end_of_week_date: string | null; // EndOfWeekDate (ISO date string)
  sync_source: string | null;   // Source of sync
  synced_at: string | null;     // Last sync timestamp
  created_at: string;           // Created timestamp
  updated_at: string;           // Updated timestamp
}

export interface TimePeriodFilters {
  period_type?: string;         // Filter by period type (Weekly, Monthly, Quarterly)
  current_year?: number;        // Filter by year
  id_pattern?: string;          // Filter by ID pattern (e.g., "W25" for all weeks in 2025)
  end_date_from?: string;       // Filter by end date from
  end_date_to?: string;         // Filter by end date to
}

export interface TimePeriodStats {
  total_periods: number;
  by_type: Record<string, number>;  // Count by period type
  years_covered: number[];
  earliest_date?: string;
  latest_date?: string;
  weekly_count: number;
  monthly_count: number;
  quarterly_count: number;
}

class TimePeriodService {
  /**
   * Get all time periods with optional filtering
   */
  async getTimePeriods(filters: TimePeriodFilters = {}): Promise<SupabaseTimePeriod[]> {
    let query = supabase
      .from('time_periods')
      .select('*')
      .order('id', { ascending: true });

    // Apply filters
    if (filters.period_type) {
      query = query.eq('period_type', filters.period_type);
    }

    if (filters.current_year) {
      query = query.eq('current_year', filters.current_year);
    }

    if (filters.id_pattern) {
      query = query.ilike('id', `${filters.id_pattern}%`);
    }

    if (filters.end_date_from) {
      query = query.gte('end_of_week_date', filters.end_date_from);
    }

    if (filters.end_date_to) {
      query = query.lte('end_of_week_date', filters.end_date_to);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get a single time period by ID
   */
  async getTimePeriodById(id: string): Promise<SupabaseTimePeriod | null> {
    const { data, error } = await supabase
      .from('time_periods')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Time period not found
      }
      throw error;
    }

    return data;
  }

  /**
   * Get time periods by type (Weekly, Monthly, Quarterly)
   */
  async getTimePeriodsByType(periodType: string): Promise<SupabaseTimePeriod[]> {
    return this.getTimePeriods({ period_type: periodType });
  }

  /**
   * Get time periods by year
   */
  async getTimePeriodsByYear(year: number): Promise<SupabaseTimePeriod[]> {
    return this.getTimePeriods({ current_year: year });
  }

  /**
   * Get weekly periods
   */
  async getWeeklyPeriods(year?: number): Promise<SupabaseTimePeriod[]> {
    const filters: TimePeriodFilters = { period_type: 'Weekly' };
    if (year) filters.current_year = year;
    return this.getTimePeriods(filters);
  }

  /**
   * Get monthly periods
   */
  async getMonthlyPeriods(year?: number): Promise<SupabaseTimePeriod[]> {
    const filters: TimePeriodFilters = { period_type: 'Monthly' };
    if (year) filters.current_year = year;
    return this.getTimePeriods(filters);
  }

  /**
   * Get quarterly periods
   */
  async getQuarterlyPeriods(year?: number): Promise<SupabaseTimePeriod[]> {
    const filters: TimePeriodFilters = { period_type: 'Quarterly' };
    if (year) filters.current_year = year;
    return this.getTimePeriods(filters);
  }

  /**
   * Get recent time periods (by end date)
   */
  async getRecentTimePeriods(limit: number = 10): Promise<SupabaseTimePeriod[]> {
    const { data, error } = await supabase
      .from('time_periods')
      .select('*')
      .order('end_of_week_date', { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get time periods statistics
   */
  async getTimePeriodStats(): Promise<TimePeriodStats> {
    const { data, error } = await supabase
      .from('time_periods')
      .select(`
        id,
        period_type,
        current_year,
        end_of_week_date
      `);

    if (error) {
      throw error;
    }

    if (!data || data.length === 0) {
      return {
        total_periods: 0,
        by_type: {},
        years_covered: [],
        weekly_count: 0,
        monthly_count: 0,
        quarterly_count: 0,
      };
    }

    // Calculate statistics
    const by_type: Record<string, number> = {};
    const years = new Set<number>();
    const dates = data.map(p => p.end_of_week_date).filter(Boolean).sort();

    data.forEach(period => {
      if (period.period_type) {
        by_type[period.period_type] = (by_type[period.period_type] || 0) + 1;
      }
      if (period.current_year) {
        years.add(period.current_year);
      }
    });

    return {
      total_periods: data.length,
      by_type,
      years_covered: Array.from(years).sort(),
      earliest_date: dates[0] || undefined,
      latest_date: dates[dates.length - 1] || undefined,
      weekly_count: by_type['Weekly'] || 0,
      monthly_count: by_type['Monthly'] || 0,
      quarterly_count: by_type['Quarterly'] || 0,
    };
  }

  /**
   * Get time period options for dropdowns
   */
  async getTimePeriodOptions(): Promise<Array<{ 
    value: string; 
    label: string; 
    type: string;
    year: number | null;
  }>> {
    const periods = await this.getTimePeriods();
    
    return periods.map(period => ({
      value: period.id,
      label: period.period_name || period.id,
      type: period.period_type || 'Unknown',
      year: period.current_year,
    }));
  }

  /**
   * Search time periods by pattern
   */
  async searchTimePeriods(searchTerm: string): Promise<SupabaseTimePeriod[]> {
    const { data, error } = await supabase
      .from('time_periods')
      .select('*')
      .or(`id.ilike.%${searchTerm}%,period_name.ilike.%${searchTerm}%,period_type.ilike.%${searchTerm}%`)
      .order('id', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get time periods grouped by type
   */
  async getTimePeriodsGroupedByType(): Promise<Record<string, SupabaseTimePeriod[]>> {
    const periods = await this.getTimePeriods();
    
    return periods.reduce((acc, period) => {
      const type = period.period_type || 'Unknown';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(period);
      return acc;
    }, {} as Record<string, SupabaseTimePeriod[]>);
  }

  /**
   * Get time periods grouped by year
   */
  async getTimePeriodsGroupedByYear(): Promise<Record<number, SupabaseTimePeriod[]>> {
    const periods = await this.getTimePeriods();
    
    return periods.reduce((acc, period) => {
      const year = period.current_year || 0;
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(period);
      return acc;
    }, {} as Record<number, SupabaseTimePeriod[]>);
  }

  /**
   * Format time period display
   */
  formatTimePeriodDisplay(period: SupabaseTimePeriod): string {
    if (period.period_name) {
      return period.period_name;
    }
    
    // Fallback formatting based on ID
    if (period.id.startsWith('W')) {
      return `Week ${period.id.substring(3)}/${period.id.substring(1, 3)}`;
    }
    if (period.id.startsWith('M')) {
      return `Month ${period.id.substring(3)}/${period.id.substring(1, 3)}`;
    }
    if (period.id.startsWith('Q')) {
      return `Q${period.id.substring(3)} ${period.id.substring(1, 3)}`;
    }
    
    return period.id;
  }

  /**
   * Get current period based on today's date
   */
  getCurrentTimePeriod(): Promise<SupabaseTimePeriod | null> {
    const today = new Date().toISOString().split('T')[0];
    
    // Find period where today falls within or close to end_of_week_date
    return supabase
      .from('time_periods')
      .select('*')
      .lte('end_of_week_date', today)
      .order('end_of_week_date', { ascending: false })
      .limit(1)
      .single()
      .then(({ data, error }) => {
        if (error && error.code !== 'PGRST116') throw error;
        return data;
      });
  }

  /**
   * Check if sync function is available and working
   */
  async testSync(): Promise<{ available: boolean; message: string }> {
    try {
      const { data, error } = await supabase.rpc('sync_timeperiods_from_api');
      
      if (error) {
        return {
          available: false,
          message: `Sync function error: ${error.message}`
        };
      }
      
      return {
        available: true,
        message: 'Sync function is available and working'
      };
    } catch (error) {
      return {
        available: false,
        message: `Sync test failed: ${error.message}`
      };
    }
  }
}

export const timePeriodService = new TimePeriodService();
export default timePeriodService;